import { useCallback, useEffect, useState } from "react";
import { supabase } from "../config";
import { readUnitBlackouts, type UnitBlackout } from "./unit-blackouts";

type ScheduleState =
  | { status: "loading" | "error"; rules: UnitBlackout[] }
  | { status: "ready"; rules: UnitBlackout[] };

const LOADING: ScheduleState = { status: "loading", rules: [] };
const TTL_MS = 60_000;
const cache = new Map<string, { at: number; promise: Promise<UnitBlackout[] | null> }>();

// Share in-flight reads per service. Failures are evicted so Retry, a remount
// or returning to the page can recover without a reload.
function fetchUnitBlackouts(serviceId: string): Promise<UnitBlackout[] | null> {
  const hit = cache.get(serviceId);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.promise;
  const promise = (async () => {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 8_000);
    try {
      const { data, error } = await supabase
        .from("availability_rules")
        .select("service_id,service_unit_id,is_enabled,applies_widget,kind,days_of_week,on_date,starts_on,ends_on")
        .eq("service_id", serviceId)
        .eq("is_enabled", true)
        .eq("applies_widget", true)
        .not("service_unit_id", "is", null)
        .in("kind", ["blackout_dow", "blackout_date", "blackout_range"])
        .abortSignal(ac.signal);
      return error ? null : readUnitBlackouts(data, serviceId);
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  })();
  cache.set(serviceId, { at: Date.now(), promise });
  while (cache.size > 12) cache.delete(cache.keys().next().value as string);
  void promise.then((rules) => {
    if (rules === null && cache.get(serviceId)?.promise === promise) cache.delete(serviceId);
  });
  return promise;
}

/** Schedule reads follow the current bands question and refresh on visibility. */
export function useUnitBlackouts(serviceId: string, questionKey: string) {
  const [answer, setAnswer] = useState<{ serviceId: string; state: ScheduleState } | null>(null);
  const [nonce, setNonce] = useState(0);
  const refetch = useCallback(() => {
    cache.delete(serviceId);
    setAnswer({ serviceId, state: LOADING });
    setNonce((n) => n + 1);
  }, [serviceId]);

  useEffect(() => {
    let live = true;
    let sequence = 0;
    const read = async () => {
      const mine = ++sequence;
      const rules = await fetchUnitBlackouts(serviceId);
      if (live && mine === sequence) {
        setAnswer({ serviceId, state: rules === null ? { status: "error", rules: [] } : { status: "ready", rules } });
      }
    };
    const visible = () => {
      if (document.visibilityState === "visible") void read();
    };
    void read();
    const timer = setInterval(visible, TTL_MS);
    document.addEventListener("visibilitychange", visible);
    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [serviceId, questionKey, nonce]);

  return { ...(answer?.serviceId === serviceId ? answer.state : LOADING), refetch };
}
